import { Box } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import sortBy from 'lodash/sortBy'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'

import CustomFields from 'modules/custom_resources/store/collections/customFields/fields'
import type CustomField from 'modules/custom_resources/store/models/customFields/Field'
import { ChoiceOption } from 'modules/custom_resources/store/models/customFields/Field'
import QuestionsWithoutCategory from 'modules/enps/pages/AnswersPage/Questions/QuestionsWithoutCategory'
import questionConfigs from 'modules/surveys/store/collections/questionConfigs'
import questionGroupConfigs from 'modules/surveys/store/collections/questionGroupConfigs'
import type QuestionConfig from 'modules/surveys/store/models/QuestionConfig'

interface Props {
  formId: number
}

function ResponseDraftQuestions({ formId }: Props) {
  const questionGroupConfig = questionGroupConfigs.find({
    surveys_form_id: formId,
  })

  const { loading } = useAsyncData(
    () =>
      Promise.all([
        questionConfigs.fetch({
          remove: false,
          data: {
            surveys_question_group_config_id: formId,
            surveys_form_id: formId,
          },
        }),
        questionGroupConfigs.fetch({
          data: {
            surveys_form_id: formId,
          },
        }),
      ]),
    [formId]
  )

  const createForm = (field: CustomField, value: string | null | boolean | number) => {
    const { id, field_type, label, min_value, max_value } = field.attributes

    const values = {
      field_id: id,
      field_type: field_type,
      mandatory: false,
      name: label,
      min_value: min_value,
      max_value: max_value,
      value,
    }

    return new Form(values, {
      field_id: 'number',
      field_type: 'string',
      mandatory: 'boolean',
      name: 'string',
      min_value: 'number',
      max_value: 'number',
      value: 'string',
    })
  }

  const customFieldsWithoutCategory = new CustomFields(
    questionConfigs.models.map((payload: QuestionConfig) => {
      const { custom_field, position } = payload.attributes

      if (!custom_field) return payload

      return { ...custom_field, position: position }
    }),
    {
      model_ref: 'surveys-question_group',
      instance_id: formId,
    }
  )

  const optionsWithoutCategory = () => {
    return customFieldsWithoutCategory.models.reduce<Record<string, ChoiceOption[]>>((memo, cf) => {
      memo[cf.attributes.id] = cf.choiceOptions.filter((opt) => opt.isActive)

      return memo
    }, {})
  }

  const formsWithoutCategory = sortBy(
    customFieldsWithoutCategory.models,
    (cf) => cf.attributes.position
  ).map((field) => {
    return createForm(field, '')
  })

  if (loading) return <Loading />

  if (!questionGroupConfig || !questionConfigs?.models?.length) return null

  return (
    <>
      {!!customFieldsWithoutCategory.length && (
        <Box gap="s48" padding="s24">
          {formsWithoutCategory.map((form: Form, index: number) => {
            const field = customFieldsWithoutCategory.get(Number(form.get('field_id').value))

            if (!field) {
              return null
            }

            return (
              <QuestionsWithoutCategory
                options={optionsWithoutCategory()}
                form={form}
                field={field}
                isResponseValues
                key={index}
              />
            )
          })}
        </Box>
      )}
    </>
  )
}

export default observer(ResponseDraftQuestions)
